import { BountyCategory } from '@foodfluence/shared';
import { IconType } from 'react-icons';
import { FiGrid, FiInstagram, FiLink, FiUser } from 'react-icons/fi';
import { MdBusiness } from 'react-icons/md';

export type ProposalFieldKey = 'companyName' | 'website' | 'instagramHandle' | 'category' | 'proposalNote';

export type ProposalFormValues = {
  companyName: string;
  website: string;
  instagramHandle: string;
  category: BountyCategory | '';
  proposalNote: string;
};

export type ProposalFieldMeta = {
  key: ProposalFieldKey;
  label: string;
  icon: IconType;
  helper?: string;
};

export const PROPOSAL_FIELDS: Record<ProposalFieldKey, ProposalFieldMeta> = {
  companyName: { key: 'companyName', label: 'Company / business name', icon: MdBusiness, helper: 'Start typing to search and select a brand' },
  website: { key: 'website', label: 'Website', icon: FiLink, helper: 'Please enter the official website URL' },
  instagramHandle: { key: 'instagramHandle', label: 'Instagram handle', icon: FiInstagram, helper: 'Please enter the official Instagram handle' },
  category: { key: 'category', label: 'Category', icon: FiGrid },
  proposalNote: { key: 'proposalNote', label: 'Who do you know there?', icon: FiUser },
};

export const CATEGORY_OPTIONS: { value: BountyCategory; label: string }[] = [
  { value: 'cpgBrand', label: 'CPG Brands' },
  { value: 'restaurant', label: 'Restaurants' },
  { value: 'hotelsLodging', label: 'Hotels & lodging' },
  { value: 'other', label: 'Other' },
];

export const stripProposalHandle = (handle: string): string => handle.trim().replace(/^@+/, '');

export const looksLikeProposalUrl = (value: string): boolean =>
  /^([a-z][a-z0-9+.-]*:\/\/)?[\w-]+(\.[\w-]+)+/i.test(value.trim());

export function validateProposalForm(values: ProposalFormValues): Partial<Record<ProposalFieldKey, string>> {
  const next: Partial<Record<ProposalFieldKey, string>> = {};
  if (!values.companyName.trim()) next.companyName = 'Company / business name is required';
  if (!values.website.trim()) next.website = 'Website is required';
  else if (!looksLikeProposalUrl(values.website)) next.website = 'Enter a valid URL, e.g. https://example.com';
  if (!values.instagramHandle.trim()) next.instagramHandle = 'Instagram handle is required';
  else if (stripProposalHandle(values.instagramHandle).length < 2) next.instagramHandle = 'Enter the handle, e.g. @saiebeauty';
  if (!values.category) next.category = 'Choose a category';
  if (!values.proposalNote.trim()) next.proposalNote = 'Tell us who you know there';
  return next;
}

export function proposalToFormValues(proposal: {
  companyName: string;
  website?: string;
  category: BountyCategory;
  instagramHandle?: string;
  proposalNote?: string;
}): ProposalFormValues {
  return {
    companyName: proposal.companyName,
    website: proposal.website ?? '',
    category: proposal.category,
    instagramHandle: proposal.instagramHandle ?? '',
    proposalNote: proposal.proposalNote ?? '',
  };
}

export function formValuesToSubmitParams(values: ProposalFormValues) {
  return {
    companyName: values.companyName.trim(),
    website: values.website.trim(),
    category: values.category as BountyCategory,
    instagramHandle: stripProposalHandle(values.instagramHandle),
    proposalNote: values.proposalNote.trim(),
  };
}
